<script setup lang="ts">
import { computed, watch, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ENQUIRY_STEP_TESTING_VARIANT, EnquiryStepEnum } from '~/enum/enquiry'
import { isEmpty, isDeepEqual } from '~/helper'
import { storeStepData } from '~/helper/enquiry'
import DJButton from '~/components/ui/button/Button.vue'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'

defineProps({
  // Top position
  top: {
    type: Number,
    default: 0,
  },
  // Visible of form
  visible: {
    type: Boolean,
    default: false,
  },
})
const cssLabelField = 'fw-600 fs-18 lh-28 mb-fs-16 mb-lh-24 mb-4 db gray-950'
const firstIndex = 0
const COUNTRY_CODE_AUSTRALIA = 'AU'

const enquiryCaptureStore = useEnquiryCaptureStore()
const route = useRoute()
const router = useRouter()
const dataStep = reactive({
  suggestion: '',
})
const errors = reactive({
  suggestion: false,
  root: false,
})

const routePathKey = computed(() => `${route.path}/design`)
const arrayStepEnquiry = computed(() => enquiryCaptureStore.arrayStep)
const stepOld = computed(() => enquiryCaptureStore.stepsData)
const stepBefore = computed(() => {
  if (stepOld.value && stepOld.value[routePathKey.value] && stepOld.value[routePathKey.value][EnquiryStepEnum.YourStyle])
    return stepOld.value[routePathKey.value][EnquiryStepEnum.YourStyle]
  return {}
})
const enquiryID = computed(() => {
  if (stepOld.value && stepOld.value[routePathKey.value]) return stepOld.value[routePathKey.value].enquiry_id
  return null
})

const handleSubmit = async () => {
  if(validationField()) return
  if (enquiryID.value) {
    // Update
    const payload = {
      path: routePathKey.value,
      step: EnquiryStepEnum.YourStyle,
      [EnquiryStepEnum.YourStyle]: {
        suggestion: dataStep.suggestion,
      },
    }
    const stepEdit = isDeepEqual(stepBefore.value, dataStep)

    if (!stepEdit) {
      const step = await enquiryCaptureStore.submitEnquiryCapture({ payload, dataStep })
      if (step?.status === 200) {
        await redirectURL()
      } else {
        errors.root = step?.message || ''
        throw new Error(`submit enquiry step ${EnquiryStepEnum.YourStyle} error (data):`, {
          cause: {
            data: step,
            payload: payload,
          },
        })
      }
    } else {
      await redirectURL()
    }
  } else {
    // New enquiry & set data for step 1
    const stepsData = {
      [EnquiryStepEnum.YourStyle]: { ...toRaw(dataStep) },
      // form_token,
      current_step: EnquiryStepEnum.YourStyle,
      next_step: arrayStepEnquiry.value[firstIndex],
      path: routePathKey.value,
    }

    const sessionData = storeStepData(stepsData)
    enquiryCaptureStore.setStepsData(sessionData)
    await redirectURL()
  }
}

const redirectURL = async () => {
  await router.push({
    name: 'enquiry-submit-trip',
    params: { trip: route.params.trip },
  })
}

const validateSuggestion = () => {
  errors.suggestion = isEmpty(dataStep.suggestion)
}

const validationField = () => {
  validateSuggestion()
  return errors.suggestion
}

function getArrayStepEnquiry() {
  let result = ENQUIRY_STEP_TESTING_VARIANT
  // let result = ENQUIRY_STEP_BASE
  // const country_code = getCookie('country_code')
  // if (route.name === 'withintrepid' || country_code !== COUNTRY_CODE_AUSTRALIA) {
  //   result = ENQUIRY_STEP_TESTING_VARIANT
  // } 
  enquiryCaptureStore.setArrayStep(result)
}

watch(
  () => stepOld.value,
  (value) => {
    if (!isEmpty(value) && (value[`${route.path}/design`] || value[route.path])) {
      const data = value[`${route.path}/design`] || value[route.path]
      if (data[EnquiryStepEnum.YourStyle]) {
        dataStep.suggestion = data[EnquiryStepEnum.YourStyle].suggestion
      }
    }
  },
)

onMounted(() => {
  getArrayStepEnquiry()
})
</script>

<template>
    <div class="bg-gray-900 sticky top-114 br-10 mx-16 bd-1 bd-gray-200-brand mb-32 all-ease sd-5" :class="visible ? 'o-100' : 'o-0 h-0'">
      <div class="bd-b-1 flex items-center justify-between br-t-10 px-16 py-12 bd-gray-200-brand" style="background-color: #f7f7f7">
        <div class="dib black fs-20 lh-28 fw-700 mr-15">Quickly Customise This Itinerary</div>
      </div>
      <form class="pa-16">
        <label :class="cssLabelField" ref="monthOfTravelRef"> Is there anything you'd like to remove or add from the selected itinerary? </label>
        <div :class="cssLabelField">
          <textarea
            placeholder="..."
            v-model="dataStep.suggestion"
            @input="validateSuggestion()"
            class="db wp-100 h-80 pt-13 pb-11 pl-16 pr-40 br-6 bd-1 bd-gray-300-brand fs-14 fw-400 lh-17 wp-100 outline-0"
          ></textarea>
          <div class="error-message" v-if="errors.suggestion">Let us know what you'd like to change.</div>
        </div>
        <div class="mt-8 fw-400 lh-24 fs-14 lh-20 gray-700 mb-fs-14 mb-lh-20">
          <span>A few ideas to get you started: </span>
          <ul class="px-16 py-4">
            <li>Are there any special sites or activities you'd like to see or do?</li>
            <li>Is this trip for a specific occasion or event?</li>
            <li>Is there anything that made a previous trip extra special?</li>
            <li>Is there anything else you'd like to tell us about this trip?</li>
          </ul>
        </div>
      </form>
      <div v-if="errors.root" class="pb-30 red error-bottom">{{ errors.root }}</div>
      <div class="bd-t-1 br-b-10 px-16 py-12 bd-gray-200-brand">
        <DJButton
          class="wp-100"
          size="large"
          type="accent"
          @click="handleSubmit"
        >
          <span class="text-md-sb">Customise Today</span>
        </DJButton>
      </div>
    </div>
</template>

<style lang="scss" scoped>
ul {
  list-style-type: disc;

  li {
    margin-left: 8px;
    margin-bottom: 6px;
  }
}
</style>
